import { AxiosError } from 'axios';
import { reactive } from 'vue';
import type { ActivityModel } from '@/models/activity';
import type { CaseModel } from '@/models/case';
import type { EntryModel } from '@/models/entry';
import {
  FormFieldType,
  type FormField,
  type FormModel,
  type FormTranslation,
  MatrixColumnType
} from '@/models/form';
import type { LoadingStateModel } from '@/models/loadingState';
import type { SaveConflict } from '@/components/data/entry/saveConflict';
import { API_ERROR_CODES, generateHexIdentifier } from '@/utils';
import type { RequestToken } from './requestToken';

/**
 * Opening an entry (ENG-9073, G4). `openEntry` closes the previous entry session, assigns a fresh
 * per-entry state, claims a request token, then reads, and says what it ended on. It takes the
 * services and the state as arguments, so a test can hold two reads in flight without Pinia; the
 * entry store's `openEntry` is the wrapper that hands it the real ones.
 *
 * The status codes the entry's reads and saves answer with are read here and nowhere else.
 */

/**
 * What an open ended on, which `Entry.vue` draws as one of ADR 0010's screens: `notFound` as
 * `refused`, with the same text, since an API that hides what exists answers 404 for forbidden, and
 * `tokenGone` as `closed` with a line of its own saying the link no longer works. `failed` is any
 * other error, left to the loader's toast.
 *
 * Nothing returns `tokenGone` yet. An empty token read is also a participant's first visit
 * (docs/memory/entry-open.md), and until the API tells a dropped token from one no entry carried
 * yet (ENG-9035) there is no answer to read it from.
 */
export type OpenOutcome = 'opened' | 'refused' | 'notFound' | 'closed' | 'tokenGone' | 'failed';

/**
 * What `openEntry` resolves to: the outcome, or `superseded` when a later open claimed the token
 * before this one's reads came back. A superseded open wrote nothing, and the later one owns the
 * screen, so its caller leaves the screen alone too.
 */
export type OpenResult = OpenOutcome | 'superseded';

/**
 * What a save ended on. `conflict` is the API's version check refusing an edit made against an
 * `updatedAt` the entry has moved on from (ENG-9078). Nothing returns `closed` yet: spdata-api
 * resolves a closed form's message on the form read, and its save endpoints do not refuse a closed
 * form with a status of their own.
 */
export type SaveOutcome = 'saved' | 'conflict' | 'closed' | 'refused' | 'failed';

const statusOf = (error: unknown) => error instanceof AxiosError ? error.response?.status : undefined;

/**
 * The outcome of a save the API answered with `error`. Only an edit carries a version, so only an
 * edit's 409 is a conflict. A create's 409 is a `uri` that already has an entry, which has no
 * version to reload, and is `failed`.
 */
export const saveOutcomeOf = (error: unknown, save: 'create' | 'edit'): Exclude<SaveOutcome, 'saved'> => {
  switch (statusOf(error)) {
    case 409:
      return save === 'edit' ? 'conflict' : 'failed';
    case 403:
    case 404:
      return 'refused';
    default:
      return 'failed';
  }
};

/**
 * Everything the store holds about the entry on screen. `openEntry` assigns a fresh one whole, so
 * no path into an entry can leave a key from the last one behind. What outlives an open (the entry
 * session, the loader, the entry context a dialog sets) is not in here.
 */
export interface EntryState {
  form: FormModel | undefined;
  /** The form as it loaded, which `isEntryPristine` compares against. */
  initialForm: FormModel | undefined;
  entry: EntryModel | undefined;
  entryLocked: Date | null | undefined;
  entryActivities: ActivityModel[] | undefined;
  /** The sign history's read failed, so its dialog says so rather than showing an empty table. */
  entryActivitiesFailed: boolean;
  selectedLanguage: FormTranslation | undefined;
  /**
   * Settles once this open's conditions pass has run, so anything that needs the *final* set of
   * fields can wait for it rather than race the timeout the pass runs on.
   *
   * The pass is deferred on purpose, so the virtualizer can size a full list, which means that
   * between the form resolving and this settling, `form.items` still holds every conditionally
   * hidden field. Anything that counts, indexes or scrolls the rendered list in that window is
   * working from a list that is about to change under it.
   *
   * Resolved, not pending, in a fresh state: an open that ends before the pass is scheduled must
   * not leave a waiter hanging.
   */
  effectsSettled: Promise<void>;
  /** A public edit link found its entry, which then opens in view mode. */
  entryTokenPreview: boolean;
  anonymousEntrySubmitError: string | undefined;
  /** What the last open ended on. Unset while it reads. */
  openOutcome: OpenOutcome | undefined;
  formClosedMessage: string;
  /** The refusal was a public form that stopped taking answers, which draws its own text. */
  anonymousResponsesNotAllowed: boolean;
  /** The field items as the last conditions pass saw them, so the store's watch runs one per change. */
  oldFormItems: FormField[] | undefined;
  /** A public entry's cached draft was restored into the form, which says so for a minute. */
  restoredEntryData: boolean;
  /**
   * The identifiers of the fields the reader has changed through a control, and of those Save
   * counted. Only the error display reads it (ADR 0011).
   */
  touchedFields: Set<string>;
  /**
   * The identifiers of the fields any write has changed, a calculation's included. Only the effect
   * engine reads it (ADR 0011).
   */
  changedFields: Set<string>;
  /**
   * Set when a save ended in a conflict and the server's version was reloaded over the reader's: the
   * fields they had changed and what they had put in, for them to re-enter by hand (ENG-9073
   * decision 6). The next open, the re-read after a save included, drops it.
   */
  saveConflict: SaveConflict | undefined;
}

export const freshEntryState = (): EntryState => ({
  form: undefined,
  initialForm: undefined,
  entry: undefined,
  entryLocked: undefined,
  entryActivities: undefined,
  entryActivitiesFailed: false,
  selectedLanguage: undefined,
  effectsSettled: Promise.resolve(),
  entryTokenPreview: false,
  anonymousEntrySubmitError: undefined,
  openOutcome: undefined,
  formClosedMessage: '',
  anonymousResponsesNotAllowed: false,
  oldFormItems: undefined,
  restoredEntryData: false,
  touchedFields: new Set(),
  changedFields: new Set(),
  saveConflict: undefined
});

/**
 * Where an opened entry's answers came from: its own saved ones, blanks for a new entry, a public
 * entry's cached draft, or a saved entry a new one copies. Only a blank entry takes the form's
 * defaults, so they never overwrite an answer someone gave (ADR 0012). A draft is written at Save,
 * after its own open took the defaults, so a field it leaves empty is one the respondent cleared.
 */
export type AnswersFrom = 'saved' | 'blank' | 'draft' | 'copy';

/** Which entry to open, read off the route or the dialog's context. */
export interface EntryOpenRequest {
  formIdentifier: string;
  entryIdentifier?: string;
  /** The case the form is read for, a public entry's `cid` included. */
  caseIdentifier?: string;
  /**
   * The case drawn beside the entry, whose read carries the case lock. A public entry carries a
   * `cid` for attribution only and has none.
   */
  selectedCaseIdentifier?: string;
  studyIdentifier?: string;
  token?: string;
  /** A form preview (`meta.entryPreview`): it has no entry session, and no entry rule applies. */
  preview: boolean;
  /** A public entry (`meta.publicEntry`), which draws the Studypages branding under its fields. */
  publicEntry: boolean;
  /**
   * The saved entry a new, unsaved entry copies its answers from, read through the entry read, which
   * returns true dates where the list row holds shifted ones (ADR 0012).
   */
  copyFrom?: string;
}

export interface EntryOpenServices {
  readForm(request: EntryOpenRequest): Promise<FormModel>;
  readEntry(entryIdentifier: string, form: FormModel): Promise<EntryModel>;
  readEntryByToken(token: string, form: FormModel): Promise<EntryModel | undefined>;
  /** The case drawn beside the entry. The store answers from a case it already holds before it reads. */
  readCase(caseIdentifier: string): Promise<CaseModel | undefined>;
}

/** The store's entry sessions, as far as opening an entry drives them. */
export interface EntrySessions {
  /** Whether the open session is on the saved entry `request` names, so opening it starts nothing. */
  holds(request: EntryOpenRequest): boolean;
  /** Ends the open session, if there is one. */
  close(): void;
  start(request: EntryOpenRequest): void;
  /**
   * The open session, if there is one, is now on this saved entry: a public edit link found it, or
   * a create made it.
   */
  recordEntry(entryIdentifier: string): void;
}

export interface EntryOpenHost {
  services: EntryOpenServices;
  state: EntryState;
  requests: RequestToken;
  loader: LoadingStateModel;
  sessions: EntrySessions;
  /** Fills a new entry's answers with blanks, then a public entry's cached draft, and says which it ended on. */
  startNewEntry(form: FormModel): 'blank' | 'draft';
  /**
   * Runs once every read is in and still wanted: the file metadata, the conditions pass, the OPEN
   * metric. `stillCurrent` turns false once a later open starts, for work that outlives this call.
   */
  finishOpen(stillCurrent: () => boolean, answersFrom: AnswersFrom): void;
}

/** The open stops at a read the API refused: `outcome` says how the screen draws it. */
class ReadRefusal extends Error {
  constructor(readonly outcome: 'refused' | 'notFound') {
    super(outcome);
  }
}

/**
 * Awaits one of the open's reads, turning a 403 into `refused` and a 404 into `notFoundAs`. The
 * case read passes `refused` for both, since it refuses the whole open whichever it answers.
 */
const readOrRefuse = async <T>(read: Promise<T>, notFoundAs: ReadRefusal['outcome']): Promise<T> => {
  try {
    return await read;
  } catch (error) {
    const status = statusOf(error);
    if (status === 403) {
      throw new ReadRefusal('refused');
    }
    if (status === 404) {
      throw new ReadRefusal(notFoundAs);
    }
    throw error;
  }
};

const isAnonymousResponsesRefusal = (error: unknown) =>
  error instanceof AxiosError &&
  error.response?.status === 401 &&
  error.response?.data?.error_code === API_ERROR_CODES.ANONYMOUS_RESPONSES_NOT_ALLOWED;

/** A field the client adds to the form it drew: the bottom Save, or a public entry's branding. */
const addClientField = (form: FormModel, fieldType: FormFieldType) => {
  form.items?.push({
    id: '',
    type: fieldType,
    identifier: generateHexIdentifier(6),
    label: '',
    description: '',
    strictRequired: false,
    nonStrictRequired: false,
    show: true,
    disable: false,
    options: {},
    default: '',
    formulaString: '',
    order: form.items ? form.items.length + 1 : 0,
    expanded: true
  });
};

/**
 * Empties a copy's file fields and matrix file cells. A copy that kept its source's file ids would
 * save a second entry pointing at the same Stored files (ADR 0012).
 */
const emptyFileAnswers = (form: FormModel) => {
  form.items?.forEach(field => {
    if (field.type === FormFieldType.FILE) {
      field.fileIds = undefined;
    }
    if (field.type === FormFieldType.MATRIX) {
      field.matrixTableRowValues?.forEach(row => row.columnValues
        ?.filter(column => column.type === MatrixColumnType.FILE)
        .forEach(column => {
          column.fileIds = undefined;
          column.textValue = undefined;
        }));
      // The saved values the rows were built from, which the matrix item builds them from again.
      const fileColumns = new Set(field.options.columns
        ?.filter(column => column.type === MatrixColumnType.FILE)
        .map(column => `${column.id}`));
      field.matrixValues?.forEach(row => {
        row.values = row.values.filter(value => !fileColumns.has(`${value.id}`));
      });
    }
  });
};

/**
 * Opens the entry `request` names. A reply that no longer holds this open's token is dropped
 * silently, with its error and its `stopLoadingAndFinish`, as `entryList.ts` does: a reader clicking
 * through entries leaves replies behind as a matter of course, and none of them is an error.
 *
 * Opening the entry the session already holds, which is the re-read after a save, keeps the saved
 * form on screen while it reads (ENG-9126). Its reads go into a fresh state of their own, which
 * lands on `state` whole once they are in, and the loader reads `reloading`, so `Entry.vue` draws
 * no page spinner over the form. Any other open clears the screen first and reads `first-load`.
 */
export const openEntry = async (host: EntryOpenHost, request: EntryOpenRequest): Promise<OpenResult> => {
  const { services, state, requests, loader, sessions } = host;

  // The close goes first: it reads the session the new one is about to replace, and the preview's
  // metrics gate is that no session is ever started for it (docs/memory/form-preview.md).
  const continuing = sessions.holds(request);
  if (!continuing) {
    sessions.close();
    Object.assign(state, freshEntryState());
    // A first load, whatever the last entry left the loader on, so the page spinner takes the screen.
    loader.reset();
  }
  if (!continuing && !request.preview) {
    sessions.start(request);
  }
  /**
   * Where this open writes: `state` itself, or for a re-read a state of its own until `landOnState` copies it across
   * whole and points this at `state`. Reactive, so the form read through it is the proxy `state.form` hands the screen
   * once it lands, and a write made through it after that is seen. The copy relies on a reactive set storing the raw
   * value, so a key added to `EntryState` that `reactive` would wrap differently from `state`'s own needs a look here.
   */
  let writeTo: EntryState = continuing ? reactive(freshEntryState()) as EntryState : state;
  /** Copies a re-read's state onto `state`, once: a call after the first, or on any other open, does nothing. */
  const landOnState = () => {
    if (writeTo !== state) {
      Object.assign(state, writeTo);
      writeTo = state;
    }
  };
  const token = requests.claim();
  const stillCurrent = () => requests.holds(token);

  loader.startLoading();
  try {
    const readForm = await services.readForm(request);
    if (!stillCurrent()) return 'superseded';
    writeTo.form = readForm;
    // The state's own copy from here, so every change below is one the screen sees.
    const form = writeTo.form;

    // Entry closed is an entry rule, and a preview has no case for one to apply to. The API sends a
    // closed form in full, so the preview draws its fields.
    if (form.closedMessage && !request.preview) {
      writeTo.formClosedMessage = form.closedMessage;
      writeTo.openOutcome = 'closed';
      return 'closed';
    }
    if (form.showSaveEntryButtonAtBottom) {
      addClientField(form, FormFieldType.SUBMIT_BUTTON);
    }
    if (request.publicEntry) {
      addClientField(form, FormFieldType.BRANDING);
    }
    writeTo.selectedLanguage = form.formTranslations?.[0];

    /** Fills the form's answers and says where they came from, or `superseded` if a later open took over. */
    const fillAnswers = async (): Promise<AnswersFrom | 'superseded'> => {
      if (request.entryIdentifier) {
        const entry = await readOrRefuse(services.readEntry(request.entryIdentifier, form), 'notFound');
        if (!stillCurrent()) return 'superseded';
        writeTo.entry = entry;
        writeTo.entryLocked = entry.lockedAt;
        return 'saved';
      }
      // Before the token: a copy is what the reader asked for, whatever else the route carries.
      if (request.copyFrom) {
        // The source's answers land in the form, and the source itself is not kept: this is a new
        // entry. It skips `startNewEntry`, whose blanks and public draft would overwrite them.
        await readOrRefuse(services.readEntry(request.copyFrom, form), 'notFound');
        if (!stillCurrent()) return 'superseded';
        emptyFileAnswers(form);
        return 'copy';
      }
      if (request.token && form.editExistingEntry) {
        const entry = await readOrRefuse(services.readEntryByToken(request.token, form), 'notFound');
        if (!stillCurrent()) return 'superseded';
        if (entry) {
          writeTo.entry = entry;
          writeTo.entryLocked = entry.lockedAt;
          writeTo.entryTokenPreview = true;
          sessions.recordEntry(entry.identifier);
          return 'saved';
        }
      }
      // The store fills a new entry's answers into its own form, so the open lands before it does.
      landOnState();
      return host.startNewEntry(form);
    };
    const answersFrom = await fillAnswers();
    if (answersFrom === 'superseded') return 'superseded';

    if (request.selectedCaseIdentifier) {
      const selectedCase = await readOrRefuse(services.readCase(request.selectedCaseIdentifier), 'refused');
      if (!stillCurrent()) return 'superseded';
      form.selectedCase = selectedCase;
    }

    writeTo.openOutcome = 'opened';
    landOnState();
    host.finishOpen(stillCurrent, answersFrom);
    return 'opened';
  } catch (error) {
    if (!stillCurrent()) return 'superseded';
    if (error instanceof ReadRefusal) {
      // Nothing of the entry stays, so nothing can draw a form, an Edit or a Save from it.
      Object.assign(writeTo, freshEntryState(), { openOutcome: error.outcome });
      return error.outcome;
    }
    if (isAnonymousResponsesRefusal(error)) {
      Object.assign(writeTo, freshEntryState(), { openOutcome: 'refused', anonymousResponsesNotAllowed: true });
      return 'refused';
    }
    loader.setError(error);
    writeTo.openOutcome = 'failed';
    return 'failed';
  } finally {
    if (stillCurrent()) {
      landOnState();
      loader.stopLoadingAndFinish();
    }
  }
};
